import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { publicApi } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, TrendingUp, AlertCircle, ExternalLink } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

const formatCurrency = (value, currency = 'GBP') => {
  const symbols = { GBP: '£', USD: '$', AED: 'AED ', INR: '₹', MUR: 'Rs ' };
  const symbol = symbols[currency] || '£';
  
  if (value >= 1000000) {
    return `${symbol}${(value / 1000000).toFixed(1)}M`;
  } else if (value >= 1000) {
    return `${symbol}${(value / 1000).toFixed(0)}K`;
  }
  return `${symbol}${value.toFixed(0)}`;
};

const SharedSignalRoom = () => {
  const { linkId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await publicApi.getSharedSignalRoom(linkId);
        setData(response.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'This link is no longer valid');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [linkId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="mt-4 text-muted-foreground">Loading Signal Room...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <Card className="w-full max-w-md glass-heavy border-destructive/50">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-destructive/20 flex items-center justify-center mb-4">
              <AlertCircle className="h-6 w-6 text-destructive" />
            </div>
            <CardTitle>Link Invalid</CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button variant="outline" onClick={() => window.location.href = 'mailto:debu@shimpartners.com'}>
              Request Access
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const currency = data?.currency || 'GBP';

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border/50 glass-heavy sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">{data?.org_name}</h1>
            <p className="text-sm text-muted-foreground">Signal Room / Read Only</p>
          </div>
          <Badge variant="outline" className="text-xs">
            Shared View
          </Badge>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="bento-grid">
          {/* Hero Card - ASBC Headline */}
          <Card className="bento-item-hero glass-heavy border-primary/30">
            <CardContent className="p-8 flex flex-col justify-center h-full">
              <p className="text-sm font-medium text-muted-foreground mb-2">
                Annual Transformation Potential
              </p>
              <h2 className="text-5xl md:text-6xl font-bold tracking-tight gradient-text mb-4">
                {formatCurrency(data?.asbc_gap || 0, currency)}
              </h2>
              <p className="text-sm text-muted-foreground">
                Directional estimate +/- 25%
              </p>
              <div className="flex items-center gap-2 mt-4">
                <TrendingUp className="h-4 w-4 text-warning" />
                <span className="text-sm text-warning font-medium">
                  {data?.velocity_label || 'Stable'}
                </span>
                <span className="text-sm text-muted-foreground">
                  +{formatCurrency(data?.cost_of_inaction_quarterly || 0, currency)}/quarter if no action
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Confidence Score */}
          <Card className="bento-item-side glass-light">
            <CardContent className="p-6 flex flex-col justify-center h-full">
              <p className="text-sm font-medium text-muted-foreground mb-4">Confidence Score</p>
              <div className="relative w-24 h-24 mx-auto">
                <svg className="w-full h-full transform -rotate-90">
                  <circle
                    cx="48"
                    cy="48"
                    r="40"
                    fill="none"
                    stroke="hsl(var(--border))"
                    strokeWidth="8"
                  />
                  <circle
                    cx="48"
                    cy="48"
                    r="40"
                    fill="none"
                    stroke="hsl(var(--primary))"
                    strokeWidth="8"
                    strokeLinecap="round"
                    strokeDasharray={`${(data?.confidence_score || 0) * 2.51} 251`}
                  />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-2xl font-bold">
                  {data?.confidence_score || 0}
                </span>
              </div>
              <p className="text-xs text-muted-foreground text-center mt-2">
                ASBC-computed
              </p>
            </CardContent>
          </Card>

          {/* Transformation Score */}
          <Card className="bento-item-side glass-light">
            <CardContent className="p-6 flex flex-col justify-center h-full">
              <p className="text-sm font-medium text-muted-foreground mb-2">
                Transformation Potential
              </p>
              <p className="text-4xl font-bold text-primary">
                {data?.transformation_potential_score?.toFixed(1) || 0}%
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                of workforce with identified potential
              </p>
            </CardContent>
          </Card>

          {/* Trendline */}
          <Card className="bento-item-full glass-light">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">12-Month Transformation Gap Trendline</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data?.trendline || []}>
                    <XAxis 
                      dataKey="month" 
                      tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                      axisLine={{ stroke: 'hsl(var(--border))' }}
                    />
                    <YAxis 
                      tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                      axisLine={{ stroke: 'hsl(var(--border))' }}
                      tickFormatter={(value) => formatCurrency(value, currency)}
                    />
                    <Tooltip 
                      contentStyle={{
                        background: 'hsl(var(--popover))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                      formatter={(value) => [formatCurrency(value, currency), 'Gap']}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="gap" 
                      stroke="hsl(var(--primary))" 
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4, fill: 'hsl(var(--primary))' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Function Panels */}
          {data?.functions?.map((func, index) => (
            <Card 
              key={func.name} 
              className={`bento-item-third glass-light border-l-4 ${
                func.signal_state === 'red' ? 'border-l-destructive' :
                func.signal_state === 'amber' ? 'border-l-warning' :
                'border-l-accent'
              }`}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium text-sm">{func.name}</h3>
                  <div className={`signal-dot signal-dot-${func.signal_state}`} />
                </div>
                <p className="text-2xl font-bold">
                  {formatCurrency(func.gap || 0, currency)}
                </p>
                <p className="text-xs text-muted-foreground">
                  {func.gap_percentage?.toFixed(1)}% gap • {func.headcount} FTE
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Request Access CTA */}
        <div className="mt-12 text-center">
          <Card className="max-w-md mx-auto glass-heavy">
            <CardContent className="p-6">
              <h3 className="font-semibold mb-2">Want to explore further?</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Request access to the full Signal Room and Blueprint capabilities.
              </p>
              <Button className="glow-primary">
                <ExternalLink className="mr-2 h-4 w-4" />
                Request Access
              </Button>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border/50 py-6 mt-12">
        <p className="text-center text-xs text-muted-foreground">
          Powered by <span className="font-medium">reAImagination</span> • Shim Partners
        </p>
      </footer>
    </div>
  );
};

export default SharedSignalRoom;
